/**
 * Notas fiscais.
 *
 * O detalhe do documento mostra os tributos como LINHAS por item — que é a
 * decisão da §4.2 ficando visível para quem opera, não só para quem lê o
 * schema. Quando IBS e CBS crescerem na transição, aparecem mais linhas; a
 * tela não muda.
 */
import { useCallback, useEffect, useState } from "react";
import { api, podeTudoQue, type Filial, type Papel } from "../api.ts";
import { BotaoImprimir, Impresso } from "../componentes/Impresso.tsx";

interface LinhaDoc {
  id: string; modelo: string; serie: number; numero: number;
  status: string; ambiente: string; chave: string | null;
  valor_produtos: string; valor_tributos: string; volume_total: string;
  criado_em: string; autorizado_em: string | null; motivo: string | null;
  parceiro: string; itens: string;
}

interface Tributo {
  tipo: string; base: string; aliquota: string; valor: string;
  cst: string; cclasstrib: string | null;
}

interface ItemDoc {
  ordem: number; descricao: string; ncm: string; cfop: string;
  quantidade: string; precoUnitario: string; valorProduto: string;
  cstIcms: string | null; cstIbsCbs: string; cclasstrib: string;
  tributos: Tributo[];
}

const n3 = (v: string) => Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const n2 = (v: string) => Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dt = (v: string | null) => (v ? new Date(v).toLocaleDateString("pt-BR") : "—");

function pilFiscal(s: string) {
  const c =
    s === "AUTORIZADA" ? "p-ok"
    : s === "PENDENTE" ? "p-aten"
    : s === "CANCELADA" || s === "INUTILIZADA" ? "p-info"
    : "p-erro";
  return <span className={`pil ${c}`}><i />{s.toLowerCase()}</span>;
}

export function Fiscal({
  aviso, papel,
}: { aviso: (t: string) => void; papel: Papel }) {
  const [lista, setLista] = useState<LinhaDoc[] | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);

  const recarregar = useCallback(() => {
    setLista(null);
    void api.get<LinhaDoc[]>("/fiscal/documentos").then(setLista).catch(() => setLista([]));
  }, []);
  useEffect(recarregar, [recarregar]);

  return (
    <>
      <div className="cab-doc">
        <h1>Notas fiscais</h1>
        <span className="cod">{lista?.length ?? 0} documentos</span>
        {lista?.[0]?.ambiente === "HOMOLOGACAO" && (
          <span className="pil p-aten"><i />homologação · sem valor fiscal</span>
        )}
      </div>

      <div className="rolagem">
        <div className="painel">
          {lista === null ? (
            <Esq />
          ) : lista.length === 0 ? (
            <div className="vazio">
              <p>Nenhuma nota emitida. Fature um pedido em Pedidos (Alt+2).</p>
            </div>
          ) : (
            <table className="tab">
              <thead>
                <tr>
                  <th style={{ textAlign: "right" }}>Nº</th>
                  <th>Modelo</th>
                  <th>Cliente</th>
                  <th>Autorizada</th>
                  <th style={{ textAlign: "right" }}>Volume m³</th>
                  <th style={{ textAlign: "right" }}>Produtos</th>
                  <th style={{ textAlign: "right" }}>Tributos</th>
                  <th>Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lista.map((d) => (
                  <tr key={d.id}>
                    <td className="r">{d.numero}/{d.serie}</td>
                    <td className="mono">{d.modelo}</td>
                    <td>{d.parceiro}</td>
                    <td className="mono">{dt(d.autorizado_em)}</td>
                    <td className="r">{n3(d.volume_total)}</td>
                    <td className="r">{n2(d.valor_produtos)}</td>
                    <td className="r">{n2(d.valor_tributos)}</td>
                    <td>
                      {pilFiscal(d.status)}
                      {d.motivo && <div className="cod" style={{ color: "var(--erro-texto)", marginTop: 3 }}>{d.motivo}</div>}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <button
                        className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
                        onClick={() => setAberto(aberto === d.id ? null : d.id)}
                      >
                        {aberto === d.id ? "fechar" : "abrir"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {aberto && (
          <Detalhe
            id={aberto}
            aviso={aviso}
            aoMudar={recarregar}
            papel={papel}
            /* A linha da lista traz o que a rota de detalhe não devolve:
               destinatário, totais e datas. O espelho precisa dos três. */
            linha={lista?.find((l) => l.id === aberto) ?? null}
          />
        )}
      </div>
    </>
  );
}

function Detalhe({
  id, aviso, aoMudar, papel, linha,
}: {
  id: string;
  aviso: (t: string) => void;
  aoMudar: () => void;
  papel: Papel;
  linha: LinhaDoc | null;
}) {
  const [filial, setFilial] = useState<Filial | null>(null);
  useEffect(() => {
    void api.get<Filial[]>("/filiais")
      .then((f) => setFilial(f.find((x) => x.matriz) ?? f[0] ?? null))
      .catch(() => {});
  }, []);
  /**
   * Cancelar NF-e autorizada é exclusivo do DONO — a rota exige, e o botão
   * precisa dizer isso ANTES do clique. Aparecer para o escritório, aceitar a
   * justificativa de 15 caracteres e só então devolver 403 é o defeito que o
   * próprio App.tsx diz querer evitar: "o item existir e responder 403 ao ser
   * clicado é pior que não existir, porque o operador relata como defeito".
   */
  const podeCancelar = podeTudoQue(papel, "DONO");
  const [doc, setDoc] = useState<
    { numero: number; serie: number; status: string; chave: string | null;
      crt: number; ambiente: string; itens: ItemDoc[] } | null
  >(null);
  const [ocupado, setOcupado] = useState(false);
  const [justificativa, setJustificativa] = useState("");
  const [prazos, setPrazos] = useState("30,60,90");

  const carregar = useCallback(() => {
    setDoc(null);
    void api.get<typeof doc>(`/fiscal/documentos/${id}`).then(setDoc).catch(() => setDoc(null));
  }, [id]);
  useEffect(carregar, [carregar]);

  if (!doc) return <div className="painel"><Esq /></div>;

  async function gerarRecebiveis() {
    setOcupado(true);
    try {
      const lista = prazos.split(",").map((s) => Number(s.trim())).filter((n) => Number.isFinite(n));
      const r = await api.post<{ parcelas: number; total: string }>(
        `/fiscal/documentos/${id}/recebiveis`, { prazos: lista },
      );
      aviso(`${r.parcelas} parcela(s) geradas · R$ ${n2(r.total)} — veja em Financeiro`);
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao gerar recebíveis");
    } finally { setOcupado(false); }
  }

  async function cancelar() {
    setOcupado(true);
    try {
      await api.post(`/fiscal/documentos/${id}/cancelar`, { justificativa });
      aviso(`Nota ${doc?.numero} cancelada · estoque estornado com lançamento novo`);
      carregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao cancelar");
    } finally { setOcupado(false); }
  }

  return (
    <div className="painel">
      <div className="tt" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span className="rotulo">NF-e {doc.numero}/{doc.serie}</span>
        {pilFiscal(doc.status)}
        <span className="cod">CRT {doc.crt} · {doc.ambiente.toLowerCase()}</span>
        {doc.chave && <span className="cod mono" style={{ fontSize: 11 }}>{doc.chave}</span>}
        <span style={{ marginLeft: "auto" }}>
          <BotaoImprimir rotulo="Imprimir espelho" />
        </span>
      </div>

      {doc.itens.map((i) => (
        <div key={i.ordem} style={{ borderBottom: "1px solid var(--linha-grade)", padding: "10px 12px" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
            <span className="mono" style={{ color: "var(--texto-terciario)" }}>{i.ordem}</span>
            <b>{i.descricao}</b>
            <span className="cod">NCM {i.ncm} · CFOP {i.cfop}</span>
            <span className="num" style={{ marginLeft: "auto" }}>
              {n3(i.quantidade)} m³ × {n2(i.precoUnitario)} = <b>{n2(i.valorProduto)}</b>
            </span>
          </div>

          {/* Tributo como LINHA — a decisão da §4.2, visível. */}
          <table className="tab" style={{ marginTop: 8, fontSize: 13 }}>
            <thead>
              <tr>
                <th>Tributo</th>
                <th>CST</th>
                <th>cClassTrib</th>
                <th style={{ textAlign: "right" }}>Base</th>
                <th style={{ textAlign: "right" }}>Alíquota</th>
                <th style={{ textAlign: "right" }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {i.tributos.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ color: "var(--texto-terciario)" }}>
                    Nenhum tributo destacado — Simples Nacional recolhe no DAS.
                  </td>
                </tr>
              ) : i.tributos.map((t) => (
                <tr key={t.tipo}>
                  <td><span className="pil p-info"><i />{t.tipo}</span></td>
                  <td className="mono">{t.cst}</td>
                  <td className="mono">{t.cclasstrib ?? "—"}</td>
                  <td className="r">{n2(t.base)}</td>
                  <td className="r">{Number(t.aliquota).toLocaleString("pt-BR", { minimumFractionDigits: 4 })}%</td>
                  <td className="r"><b>{n2(t.valor)}</b></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {doc.status === "AUTORIZADA" && (
        <div style={{ padding: 12, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <label style={{ flex: "0 0 200px" }}>
              <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>Prazos (dias)</span>
              <input className="campo-texto mono" value={prazos} onChange={(e) => setPrazos(e.target.value)} />
            </label>
            <button className="bt" onClick={() => void gerarRecebiveis()} disabled={ocupado}>
              Gerar recebíveis
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <label style={{ flex: 1 }}>
              <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>
                Justificativa do cancelamento (mín. 15 caracteres)
              </span>
              <input
                className="campo-texto" value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                disabled={!podeCancelar}
              />
            </label>
            <button
              className="bt bt-destrutivo" onClick={() => void cancelar()}
              disabled={!podeCancelar || ocupado || justificativa.trim().length < 15}
              title={podeCancelar ? undefined : "Só o dono cancela nota autorizada"}
            >
              Cancelar nota
            </button>
          </div>
          {!podeCancelar && (
            <span className="cod">
              Cancelamento de nota autorizada é do dono — peça a ele.
            </span>
          )}
        </div>
      )}

      {/*
        Espelho da NF-e — não é DANFE, e a diferença não é semântica.
        DANFE é a representação de uma nota AUTORIZADA pela SEFAZ; esta saiu
        de emissor fake em homologação e não existe lá. Chamar de DANFE seria
        produzir um papel que se passa por documento fiscal, que é justamente
        o que a tarja impede.
      */}
      <Impresso
        emitente={{
          nome: filial?.razaoSocial ?? "—",
          cnpj: filial?.cnpj ?? "—",
          filial: filial?.nome ?? "—",
          cidade: filial?.cidade ?? "—",
          uf: filial?.uf ?? "—",
        }}
        titulo="Espelho de NF-e"
        numero={`${doc.numero}/${doc.serie}`}
        emissao={dt(linha?.criado_em ?? null)}
        rodapeEsquerda={`Situação: ${doc.status} · CRT ${doc.crt}`}
        rodapeDireita={`Ambiente: ${doc.ambiente}`}
      >
        <div className="doc-aviso">
          Documento sem valor fiscal
          <small>
            Espelho para conferência. Ambiente de {doc.ambiente.toLowerCase()}
            {" "}— esta nota não foi transmitida à SEFAZ e não acompanha carga.
          </small>
        </div>

        <div className="doc-partes">
          <div>
            <span className="rot">Emitente</span>
            <b>{filial?.razaoSocial ?? "—"}</b><br />
            CNPJ {filial?.cnpj ?? "—"}<br />
            {filial?.cidade ?? "—"}/{filial?.uf ?? "—"}
          </div>
          <div>
            <span className="rot">Destinatário</span>
            <b>{linha?.parceiro ?? "—"}</b>
          </div>
        </div>

        {doc.chave && (
          <div className="doc-chave">
            <b>Chave de acesso:</b> {doc.chave}
          </div>
        )}

        <table>
          <thead>
            <tr>
              <th>#</th><th>Descrição</th><th>NCM</th><th>CFOP</th>
              <th style={{ textAlign: "right" }}>Qtd m³</th>
              <th style={{ textAlign: "right" }}>Unit.</th>
              <th style={{ textAlign: "right" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {doc.itens.map((i) => (
              <tr key={i.ordem}>
                <td className="n">{i.ordem}</td>
                <td>{i.descricao}</td>
                <td>{i.ncm}</td>
                <td>{i.cfop}</td>
                <td className="n">{n3(i.quantidade)}</td>
                <td className="n">{n2(i.precoUnitario)}</td>
                <td className="n">{n2(i.valorProduto)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4}>
                {doc.itens.length} item(ns) · tributos R$ {n2(linha?.valor_tributos ?? "0")}
              </td>
              <td className="n">{n3(linha?.volume_total ?? "0")}</td>
              <td />
              <td className="n">{n2(linha?.valor_produtos ?? "0")}</td>
            </tr>
          </tfoot>
        </table>

        {/* Tributo como linha também no papel — é o que o §4.2 decide. */}
        {doc.itens.some((i) => i.tributos.length > 0) && (
          <table style={{ marginTop: "8pt" }}>
            <thead>
              <tr>
                <th>Item</th><th>Tributo</th><th>CST</th><th>cClassTrib</th>
                <th style={{ textAlign: "right" }}>Base</th>
                <th style={{ textAlign: "right" }}>Alíq.</th>
                <th style={{ textAlign: "right" }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {doc.itens.flatMap((i) =>
                i.tributos.map((t) => (
                  <tr key={`${i.ordem}-${t.tipo}`}>
                    <td className="n">{i.ordem}</td>
                    <td>{t.tipo}</td>
                    <td>{t.cst}</td>
                    <td>{t.cclasstrib ?? "—"}</td>
                    <td className="n">{n2(t.base)}</td>
                    <td className="n">{Number(t.aliquota).toLocaleString("pt-BR", { minimumFractionDigits: 4 })}%</td>
                    <td className="n">{n2(t.valor)}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        )}
      </Impresso>
    </div>
  );
}

function Esq() {
  return (
    <div className="esq">
      {[0, 1, 2, 3].map((i) => (
        <div className="l" key={i}>
          <span className="b" /><span className="b" /><span className="b" /><span className="b" />
        </div>
      ))}
    </div>
  );
}
